/**
 * One place for user-facing error wording.
 * Rule: say what happened + what to do next, and never show raw technical text
 * (exceptions, HTTP codes, SQL, stack traces). Curated server messages such as
 * "Insufficient available points" or field validation texts pass through unchanged.
 * The technical detail is logged to the console for troubleshooting.
 */

export const ERROR_MESSAGES = {
  generic: 'Something went wrong. Please try again later.',
  request: 'Something went wrong while processing your request. Please try again.',
  load: 'Unable to load the data. Please refresh the page and try again.',
  save: 'Unable to save the changes. Please try again.',
  remove: 'Unable to delete this record. Please try again.',
  network: 'Unable to reach the server. Please check your internet connection and try again.',
  permission: 'You do not have permission to perform this action.',
  notFound: 'The requested data could not be found.',
  invalid: 'The request could not be processed. Please check the entered details and try again.',
  timeout: 'The request took too long. Please try again.',
  tooMany: 'Too many requests. Please wait a moment and try again.',
  session: 'Authentication failed. Please login again.',
};

// Text that must never reach a user.
const TECHNICAL_PATTERNS = [
  /exception/i,
  /stack ?trace/i,
  /object reference|null ?reference/i,
  /\bsql\b|sqlclient|sql ?server|dbupdate|entity ?framework|\bdatabase\b/i,
  /connection (string|refused|reset|closed)|econn\w*|enotfound|etimedout|socket/i,
  /deadlock|foreign key|violation of|constraint/i,
  /syntaxerror|typeerror|referenceerror|rangeerror|unexpected token|unexpected end of/i,
  /is not a function|is not defined|cannot read propert\w*|cannot set propert\w*|\bundefined\b|\[object object\]/i,
  /failed to fetch|networkerror|load failed|network request failed/i,
  /http error|internal server error|bad gateway|service unavailable|gateway time-?out/i,
  /\bstatus(?: code)?[: ]+\(?[45]\d\d\b/i,
  /\bat\s+[\w.<>$]+\s*\(.*\)/,
  /\b(System|Microsoft|BrokerAffiliateAPI)\.[A-Z]\w+/,
  /\bjson\b.*\b(parse|position)\b|\bparse error\b/i,
];

export const isTechnicalMessage = (message) => {
  const text = String(message ?? '').trim();
  if (!text) return false;
  return TECHNICAL_PATTERNS.some((re) => re.test(text));
};

const logTechnical = (raw, context) => {
  // eslint-disable-next-line no-console
  if (typeof console !== 'undefined') console.error(`[user-error]${context ? ` ${context}` : ''}`, raw);
};

/**
 * Turns whatever came back (string, Error, API payload text) into a message that is safe to show.
 * Technical or empty text is replaced by `fallback`.
 */
export const toUserMessage = (raw, fallback = ERROR_MESSAGES.request) => {
  const text = String(raw?.message ?? raw ?? '').trim();
  if (!text || /^error$/i.test(text) || isTechnicalMessage(text)) {
    if (text) logTechnical(raw);
    return fallback;
  }
  return text;
};

/** HTTP status → friendly message (used when the server did not send a safe message of its own). */
export const messageForStatus = (status) => {
  switch (Number(status)) {
    case 400:
    case 422:
      return ERROR_MESSAGES.invalid;
    case 401:
      return ERROR_MESSAGES.session;
    case 403:
      return ERROR_MESSAGES.permission;
    case 404:
      return ERROR_MESSAGES.notFound;
    case 408:
    case 504:
      return ERROR_MESSAGES.timeout;
    case 429:
      return ERROR_MESSAGES.tooMany;
    default:
      return Number(status) >= 500 ? ERROR_MESSAGES.request : ERROR_MESSAGES.generic;
  }
};

/** Picks the right fallback sentence from the API call name, e.g. "Get Clients List" → load message. */
export const fallbackForApi = (apiName) => {
  const name = String(apiName || '');
  if (/\b(delete|remove)\b/i.test(name)) return ERROR_MESSAGES.remove;
  if (/^(get|load|fetch|list)\b/i.test(name)) return ERROR_MESSAGES.load;
  if (/\b(save|update|create|add|edit|set)\b/i.test(name)) return ERROR_MESSAGES.save;
  return ERROR_MESSAGES.request;
};

/** True for network-level failures (offline, DNS, CORS, server down) thrown by fetch. */
export const isNetworkError = (error) =>
  error instanceof TypeError || /failed to fetch|networkerror|load failed|network request failed/i.test(String(error?.message || ''));
